/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.19.0
 */

import { useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import type { ITableMetaResponse } from '@modules/table';
import { checkESTableList } from '@utils/checkESTable';

import {
    tableColumnSettingsUrlSelector,
    tableContext,
    tableDataLoadSizeSelector,
    tableLabelsUrlSelector,
    tableListUrlSelector,
    tableMetaFieldsDraggableSelector,
    tableMetaFieldsRequiredSelector,
    tableMetaUrlSelector,
} from '../../selectors';
import { tableApi } from '../../store';

export const useTableColumnSettings = () => {

    const dispatch = useDispatch();

    /* API */
    const [changeColumnOrderApi] = tableApi.endpoints.changeColumnOrder.useMutation();

    const metaUrl = useSelector(tableMetaUrlSelector);
    const listUrl = useSelector(tableListUrlSelector);
    const columnSettingsUrl = useSelector(tableColumnSettingsUrlSelector);
    const labelsUrl = useSelector(tableLabelsUrlSelector);
    const tableLoadSize = useSelector(tableDataLoadSizeSelector);

    /* Контекст для основных операций*/
    const context = useSelector(tableContext);

    const draggableFieldsSelector =
        useMemo(() => tableMetaFieldsDraggableSelector(metaUrl, context), [context, metaUrl]);
    const requiredFieldsSelector =
        useMemo(() => tableMetaFieldsRequiredSelector(metaUrl, context), [context, metaUrl]);

    const draggableFields = useSelector(draggableFieldsSelector);
    const requiredFields = useSelector(requiredFieldsSelector);

    const changeColumnOrder = (fields: ITableMetaResponse['fields']) => {
        if (columnSettingsUrl) {

            /* Сохранение данных на сервере */
            changeColumnOrderApi({
                url: columnSettingsUrl,
                context,
                fields,
            }).then(() => {
                if (!metaUrl || !listUrl) {
                    // eslint-disable-next-line no-throw-literal
                    throw '[Table]: incorrect table configuration. Set metaUrl and listUrl in initialize method';
                }

                /* Загрузка меток */
                if (labelsUrl) {
                    dispatch(
                        tableApi.endpoints.getLabelsList.initiate({ url: labelsUrl }, { forceRefetch: true }),
                    );
                }
                dispatch(
                    tableApi.endpoints.getTableMeta.initiate({ url: metaUrl, context }, { forceRefetch: true }),
                );
                dispatch(
                    tableApi.endpoints.getTableList.initiate({
                        url: listUrl,
                        from: 0,
                        size: tableLoadSize,
                        use_search_after: checkESTableList(listUrl),
                        context,
                    }, { forceRefetch: true }),
                );
            });
        }
    };

    return {
        draggableFields,
        requiredFields,

        changeColumnOrder,
    };
};
